import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import anime from 'animejs/lib/anime.es.js';

import { PersistentWatcherService } from 'app/services/persistent-watcher.service';
import { environment } from 'env/environment';
import { PageOverlayComponent } from "app/components/ui-kit/page-components/page-overlay/page-overlay.component";
import { WatcherCardComponent } from 'app/components/distinct-components/cards/watcher-card/watcher-card.component';
import { holdPls } from 'app/utils/time/timing-utils';
import { formatLocation } from 'app/utils/flight/flight-data-formatters';
import { normalizeBooleanFlag } from 'app/utils/boolean-normalizer';
import { HeavyHeaderComponent } from "app/components/ui-kit/typography/heavy-header/heavy-header.component";
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { BaseInputComponent } from 'app/components/ui-kit/inputs/base-input/base-input.component';
import { AiTypingComponent } from 'app/components/distinct-components/ai-visual/ai-typing/ai-typing.component';
import { PrimaryBtnComponent } from "app/components/ui-kit/buttons/primary-btn/primary-btn.component";

interface PersistentWatcherAiResponse {
  dataModel: Record<string, unknown>;
  confidenceScore?: number;
}

@Component({
  selector: 'app-add-persistent-watcher',
  standalone: true,
  imports: [
    CommonModule, FormsModule, HttpClientModule, PageOverlayComponent,
    WatcherCardComponent, HeavyHeaderComponent, SvgLoaderComponent,
    BaseInputComponent, AiTypingComponent,
    PrimaryBtnComponent
  ],
  templateUrl: './add-persistent-watcher.component.html',
  styleUrl: './add-persistent-watcher.component.scss'
})
export class AddPersistentWatcherComponent {
  prompt: string = '';
  isLoading = false;
  isSaving = false;
  error: string | null = null;
  previewWatchers: any[] = [];
  private previewIdCounter = 0;
  agentText: string = null;
  agentTypedText = '';
  private _typingInterval?: number;

  @Output() onClose = new EventEmitter<void>();

  constructor(private http: HttpClient, private watcherService: PersistentWatcherService) { }

  async onSubmit() {
    this.agentText = null;
    if (!this.prompt?.trim() || this.isLoading || this.isSaving) {
      return;
    }
    this.error = null;
    this.isLoading = true;

    try {
      const url = `https://${environment.apiBaseURL}.cloudfunctions.net/getAiPersistentSchemaResult`;

      const response = await this.http
        .post<PersistentWatcherAiResponse>(url, { userQuery: this.prompt })
        .toPromise();

      if (!response) throw new Error('Unable to generate a watcher.');

      const watcher = this.buildWatcherFromAiModel(response);

      if (watcher.departureLocationName == null || (watcher.arrivalLocationName == null && watcher.arrivalGeneralType == null)) {
        this.agentText = 'Try again. Just tell me two locations, number of seats, and preferences like no children or pets';
        this.typeOutText(this.agentText);
        return;
      }
      if (watcher.departureLocationName === 'Unknown' || (watcher.arrivalLocationName === 'Unknown' && watcher.arrivalGeneralType === 'Unknown')) {
        this.agentText = 'Try again. Just tell me two locations, number of seats, and preferences like no children or pets';
        this.typeOutText(this.agentText);
        return;
      }
      this.previewWatchers = [...this.previewWatchers, watcher];
      console.log('PREVIEWS', this.previewWatchers);
      this.prompt = '';
      await holdPls(500);
      this.animateResult();
    } catch (err) {
      console.error(err);
      const responseError = err as { error?: { error?: string } };
      this.error = responseError?.error?.error || 'Failed to generate watcher';
    } finally {
      this.isLoading = false;
    }
  }

  private buildWatcherFromAiModel(aiResult: any): any {
    const dm = aiResult?.dataModel ?? {};
    const bringingPets = normalizeBooleanFlag(dm.isBringingPets);
    const bringingChildren = normalizeBooleanFlag(dm.isBringingChildren);
    const petOk = normalizeBooleanFlag(dm?.isPetOk);
    const childrenOk = normalizeBooleanFlag(dm?.isChildrenOk);

    const watcher: any = {
      // Departure
      departureLocationName: dm.departureData?.locationName ?? null,
      departureAirportName: dm.departureData?.airportName ?? null,
      departureAirportCode: dm.departureData?.airportCode ?? null,

      // Arrival - precise
      arrivalLocationName: dm.arrivalData?.preciseDestination?.locationName ?? null,
      arrivalAirportName: dm.arrivalData?.preciseDestination?.airportName ?? null,
      arrivalAirportCode: dm.arrivalData?.preciseDestination?.airportCode ?? null,
      arrivalState: dm.arrivalData?.preciseDestination?.state ?? null,
      arrivalCountry: dm.arrivalData?.preciseDestination?.country ?? null,
      arrivalPreciseType: dm.arrivalData?.preciseDestination?.destinationType ?? null,

      // Arrival - general
      arrivalGeneralType: dm.arrivalData?.generalDestination?.destinationType ?? null,
      arrivalGeneralState: dm.arrivalData?.generalDestination?.state ?? null,
      arrivalGeneralCountry: dm.arrivalData?.generalDestination?.country ?? null,

      // Preferences
      isBringingPets: bringingPets ?? false,
      isBringingChildren: bringingChildren ?? false,
      isPetOk: petOk ?? true,
      isChildrenOk: childrenOk ?? true,

      seatsNeeded: Math.max(1, dm.seatsNeeded ?? 1),
      status: 'Ready to Save',
      isPreview: true,
    };

    const fromRaw = watcher.departureLocationName;
    let toRaw: string = watcher.arrivalLocationName;
    if (!toRaw) {
      const generalType = watcher.arrivalGeneralType || 'Location';
      const parts = [] as string[];
      if (watcher.arrivalGeneralState) {
        parts.push(watcher.arrivalGeneralState);
      }
      if (watcher.arrivalGeneralCountry) {
        parts.push(watcher.arrivalGeneralCountry);
      }
      const suffix = parts.length > 0 ? ` in ${parts.join(', ')}` : '';
      toRaw = `Any ${generalType}${suffix}`;
    }

    watcher.displayName = `${formatLocation(fromRaw)} to ${formatLocation(toRaw)}`;
    watcher._tempId = `preview-${Date.now()}-${this.previewIdCounter++}`;

    return watcher;
  }

  trackByPreviewWatcher(_index: number, watcher: any): string {
    return watcher?._tempId ?? `${_index}`;
  }

  onRemoveWatcher(watcher: any) {
    if (!watcher) {
      return;
    }

    const targetId = watcher._tempId;
    this.previewWatchers = this.previewWatchers.filter(w => w._tempId !== targetId);
  }

  async onConfirmAll() {
    if (this.previewWatchers.length === 0 || this.isLoading || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.error = null;

    const failures: string[] = [];
    const remaining: any[] = [];
    let savedCount = 0;

    try {
      for (const watcher of this.previewWatchers) {
        try {
          const payload = { ...watcher, status: 'active', isPreview: false };
          const res = await this.watcherService.createWatcher(payload);
          if (res.saved) {
            savedCount++;
            continue;
          }

          failures.push(res.reason || 'Failed to save watcher');
          remaining.push(watcher);
        } catch (err) {
          console.error(err);
          failures.push('Failed to save watcher');
          remaining.push(watcher);
        }
      }
    } finally {
      this.previewWatchers = remaining;
      this.isSaving = false;
    }

    if (failures.length === 0) {
      this.onClose.emit();
      return;
    }

    const uniqueReasons = Array.from(new Set(failures.filter(Boolean)));
    const reasonText = uniqueReasons.length > 0 ? `: ${uniqueReasons.join(', ')}.` : '.';
    const failureCount = failures.length;
    const failureLabel = failureCount === 1 ? 'watcher' : 'watchers';
    const successSuffix = savedCount > 0 ? ` ${savedCount} watcher${savedCount === 1 ? '' : 's'} added successfully.` : '';
    this.error = `Failed to save ${failureCount} ${failureLabel}${reasonText}${successSuffix}`.trim();
  }

  typeOutText(fullText: string, speed: number = 20): void {
    if (this._typingInterval != null) {
      clearInterval(this._typingInterval);
      this._typingInterval = undefined;
    }
    this.agentTypedText = '';

    setTimeout(() => {
      let index = 0;

      this._typingInterval = window.setInterval(() => {
        this.agentTypedText += fullText.charAt(index);
        index++;

        if (index >= fullText.length) {
          if (this._typingInterval != null) {
            clearInterval(this._typingInterval);
            this._typingInterval = undefined;
          }
        }
      }, speed);
    }, 0);
  }

  onCancel() {
    this.onClose.emit();
  }

  animateResult() {
    anime({
      targets: '.watcher-card.preview',
      duration: 650,
      opacity: [0, 1],
      translateY: [12, 0],
      direction: 'alternate',
      easing: 'spring(1, 80, 10, 2)',
      loop: false,
      delay: anime.stagger(35)
    });
  }
}
